// @ts-check
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  activityCodes,
  applicationTypes,
  instituteCodes,
} from 'services/inputTokens';
import { getFilter } from 'selectors/organisation';
import { setFilterGroup } from 'actions/organisation';
import { fundingKeys } from 'components/Search/Filters/keys';
import { FilterSection } from 'components/Search/Filters/styled';
import { getFundingCheckboxStates } from 'components/Search/Filters/fundingFilterRules';
import { helpText } from 'components/Search/helpers';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';
import { formatCurrency } from 'utils/number';
import { getResearcherText } from 'utils/constants';
import { Container } from 'styled';

import OrgFilterField from './OrgFilterField';
import OrgFilterSection from './OrgFilterSection';
import {
  applyOrgActivityCodeChange,
  applyOrgPresetSideEffects,
} from './orgPresetSideEffects';

const activeKeys = Object.keys(fundingKeys);

const OrgFundingSection = ({ organisationId, loading }) => {
  const dispatch = useDispatch();

  const grantExpiry = useSelector(
    getFilter(organisationId, fundingKeys.grantExpiry.key)
  );
  const earlyAwardDate = useSelector(
    getFilter(organisationId, fundingKeys.earlyAwardDate.key)
  );

  const {
    grantExpiryLocked,
    earlyAwardDateLocked,
    grantExpiryDisabled,
    earlyAwardDateDisabled,
  } = getFundingCheckboxStates({ loading, grantExpiry, earlyAwardDate });

  // Mirrors main search's Core-Labs toggle: flips the P30 activity code on/off
  // alongside the core flag so the NIH filter and the switch stay consistent.
  const handleCore = value => {
    dispatch(setFilterGroup(organisationId, fundingKeys.core.key, value));
    dispatch(applyOrgPresetSideEffects(organisationId, { core: value }));
  };

  const handleActivityCode = value => {
    dispatch(applyOrgActivityCodeChange(organisationId, value));
  };

  return (
    <OrgFilterSection
      organisationId={organisationId}
      icon='dollar'
      title='Funding'
      activeKeys={activeKeys}
    >
      <OrgFilterField
        filterType='datePicker'
        type='range'
        placeholder={['Awarded From', 'Awarded To']}
        filterId={fundingKeys.dateAwarded.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={fundingKeys.minFunding.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        formatter={formatCurrency}
        placeholder='Min. Funding (3 Years)'
        step={10000}
        min={0}
        helpText={`Filter results by the minimum 3 year funding a ${getResearcherText()} has received`}
        disabled={loading}
      />
      <OrgFilterField
        filterType='number'
        filterId={fundingKeys.maxFunding.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        formatter={formatCurrency}
        placeholder='Max. Funding (3 Years)'
        step={10000}
        min={0}
        helpText={`Filter results by the maximum 3 year funding a ${getResearcherText()} has received`}
        disabled={loading}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={fundingKeys.grantExpiry.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={
          grantExpiryLocked ? helpText.filterDisabled : helpText.grantExpiry
        }
        labelText='Funding Expiring Within 3 Months'
        disabled={grantExpiryDisabled}
      />
      <FilterSection>NIH Only</FilterSection>
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode='multiple'
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Application Type'
        filterId={fundingKeys.nihApplicationType.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        options={applicationTypes}
        disabled={loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode='multiple'
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Activity Code'
        filterId={fundingKeys.nihActivityCode.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        options={activityCodes}
        onChange={handleActivityCode}
        disabled={loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode='multiple'
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Institute Code'
        filterId={fundingKeys.nihInstituteCode.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        options={instituteCodes}
        disabled={loading}
      />
      <Container>
        <OrgFilterField
          filterType='checkbox'
          filterId={fundingKeys.earlyAwardDate.key}
          organisationId={organisationId}
          setFilterGroup={setFilterGroup}
          getFilter={getFilter}
          helpText={
            earlyAwardDateLocked
              ? helpText.filterDisabled
              : helpText.earlyAwardDate
          }
          labelText='Early Budget'
          disabled={earlyAwardDateDisabled}
        />
        <OrgFilterField
          filterType='switch'
          filterId={fundingKeys.core.key}
          organisationId={organisationId}
          setFilterGroup={setFilterGroup}
          getFilter={getFilter}
          onChange={handleCore}
          disabled={loading}
        >
          Core Labs Only
        </OrgFilterField>
      </Container>
    </OrgFilterSection>
  );
};

OrgFundingSection.propTypes = {
  organisationId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
};

export default OrgFundingSection;
